// @flow
import type { Dispatch } from 'redux';
import { RECEIVE_FACETS } from '../constants';
import type { Facets, Facet, GlobalState } from '../types';
import { getEntries, removePropertyFromObject } from '../../../helpers';
import { applyFiltering } from '../helpers';
import { facets as facetConfig, selectorByPdbProperty } from '../config';
import {
  getFacets, getRangeFilters, getAssortment, getTermFilters,
} from '../selectors';
import { getAvailableFilters } from '../../routes';

type Arguments = {|
  label: string,
  selector: (state: GlobalState, pdbId: string) => string | number,
  state: GlobalState,
  assortment: Array<string>,
|};

export const generateFacet = ({
  label,
  selector,
  state,
  assortment,
}: Arguments) => (
  // for each assortment item we call the selector for this facet
  assortment.reduce((accumulator, pdbId) => {
    const bucket = selector(state, pdbId);

    if (!bucket) {
      return {
        ...accumulator,
      };
    }

    let sanitizedBucket = bucket;
    if (typeof bucket === 'string') {
      sanitizedBucket = bucket.trim();
    }

    const previousBuckets = accumulator[label] || {};
    const previousBucketCount = (accumulator[label] && accumulator[label][sanitizedBucket]) || 0;

    const newBucketCount = previousBucketCount + 1;

    return {
      ...accumulator,
      [label]: {
        ...previousBuckets,
        [sanitizedBucket]: newBucketCount,
      },
    };
  }, {})
);

export type ReceiveFacets = (facets: Facets) => {|
  type: typeof RECEIVE_FACETS,
  payload: Facets,
|};

export type ReceiveFacetsAction = {|
  type: typeof RECEIVE_FACETS,
  payload: Facets,
|};

export const receiveFacets: ReceiveFacets = (facets: Facets) => ({
  type: RECEIVE_FACETS,
  payload: facets,
});

export const createFacets = (
  state: GlobalState,
  assortment: Array<string>,
) => facetConfig.reduce((acc, facetName) => {
  const selector = selectorByPdbProperty[facetName];
  /**
   * Below, we create the assortment that is the input
   * for generating the buckets in a facet.
   *
   * This needs to be done via applyFiltering, since the
   * input for a facet is always the full filtered assortment,
   * but without the filter that belongs to its own facet.
   *
   * Example:
   * - Imagine a resultset with two facets: Make and Transmission
   * - If you apply a Make filter, the Transmission facet
   * updates to reflect the right counts for each bucket
   * - The Make facet, however, shouldn't update, since the
   * counts reflect how many cars will be added to the result-
   * set when you check a checkbox.
   * - If you now add a Transmission filter, the Transmission facet
   * should stay the same.
   * - The Make facet, however, should update to reflect the counts
   * based on the Transmission filter.
   */
  const facetAssortment = applyFiltering(
    state,
    assortment,
    // Remove own filter from list of applied filters
    removePropertyFromObject(getTermFilters(state), facetName),
    removePropertyFromObject(getRangeFilters(state), facetName),
  );

  return {
    ...acc,
    ...generateFacet({
      label: facetName,
      selector,
      state,
      assortment: facetAssortment,
    }),
  };
}, {});

export const generateFacets = () => (dispatch: Dispatch<*>, getState: Function) => {
  const state = getState();
  const assortment = getAssortment(state);

  // generate facets for every category
  const facets = createFacets(state, assortment);
  dispatch(receiveFacets(facets));
};

export const updateFacetNumber = (facet: Facet, newFacet: Facet) => getEntries(facet)
  .reduce((acc, [bucket]) => {
    // if the facet doesn't exist in the new facet calculation
    if (!newFacet || !(bucket in newFacet)) {
      return {
        ...acc,
        [bucket]: 0,
      };
    }

    // if the facet exists then use the new count
    return {
      ...acc,
      [bucket]: newFacet[bucket],
    };
  }, {});

export const recalculateFacets = (facets: Facets, newFacets: Facets, selectedFacet?: string) => (
  getEntries(facets)
    .reduce((acc, [label, facet]) => {
      const newFacet = newFacets[label];

      // when the facet is the selected facet don't update!
      if (selectedFacet && label === selectedFacet) {
        return {
          ...acc,
          [label]: facet,
        };
      }

      return {
        ...acc,
        [label]: updateFacetNumber(facet, newFacet),
      };
    }, {})
);

/**
 * This method filters the facets based on the available filters.
 */
export const getAvailableFacets = (facets: Facets, availableFilters: Array<string>) => (
  Object.keys(facets)
    .filter(key => availableFilters.includes(key))
    .reduce((acc, key) => {
      acc[key] = facets[key];
      return acc;
    }, {})
);

/**
 * This method generates new facets based on the filtered set of assortment
 * Then it'll merge the new facets with the current facets
 */
export const updateFacetsNumbers = (selectedFacet?: string) => (
  (dispatch: Dispatch<*>, getState: Function) => {
    const state = getState();
    const assortment = getAssortment(state);

    // retrieve current facets
    let currentFacets = getFacets(state);

    // Retrieve the available filters
    const availableFilters = getAvailableFilters(state);
    // If there are a limit amount of filters available,
    if (availableFilters) {
      // we filter the current facets based on the available filters.
      currentFacets = getAvailableFacets(currentFacets, availableFilters);
    }

    // generate new facets based on filtered assortment
    const newFacets = createFacets(state, assortment);

    // recalculate facets
    const recalculatedFacets = recalculateFacets(currentFacets, newFacets, selectedFacet);

    dispatch(receiveFacets(recalculatedFacets));
  }
);
